import { useState } from "react";
import { useGstWording } from "./useGstWording";
import { useMutation, useQuery } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import { listPriceComponents, termWords } from "../../api/priceList";
import type { CallerIdentity } from "../../api/gateway";
import {
  buildPlan,
  canSavePlan,
  durationPlanUid,
  durationRequest,
  EMPTY_FORM,
  missingDurations,
  netOf,
  pick,
  pickableRows,
  planCode,
  toRequest,
  unpick,
  validate,
  type DurationGroup,
  type PlanForm,
  type PlanSpecFailure,
  type PlanSpecResult,
} from "../../api/planBuilder";

const inr = (n: number) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function newUid(): string {
  return crypto.randomUUID();
}

function failureText(e: Error): { title: string; detail: string } {
  if (e instanceof ApiError) {
    const body = e.body as PlanSpecFailure | null;
    const detail = body?.error ?? e.message;
    const step = body?.failed ? ` (at the ${body.failed} step)` : "";
    switch (e.kind) {
      case "unauthenticated":
        return { title: "Your sign-in has expired -- sign in again", detail };
      case "forbidden":
        return { title: "Refused -- your role cannot build plans here yet", detail };
      case "not-found":
        return { title: "The gateway does not publish Create plan yet", detail };
      default:
        if (e.status === 409) return { title: `Not built${step}`, detail };
        if (e.status === 422) return { title: `Not built${step}`, detail };
        if (body?.written) return { title: `Partly built${step} -- press Save again to finish it`, detail };
    }
    return { title: "Could not build the plan", detail };
  }
  return { title: "Could not build the plan", detail: e.message };
}

/**
 * Plans › Create plan (plan builder design §9.12 C5, owner 2026-10-09).
 *
 * Start from the price list -- one click fills speed, days and price from a
 * compiled row, and the plan is LINKED to that price record -- or type it in.
 * Validity counts from the first login; there is no data cap and no fixed end
 * date (owner P3/P4). The price is whole rupees including GST (P2).
 *
 * 🔑 plan_uid is made ONCE per form and resent on every Save, so a retry after
 * a timeout finishes the same plan instead of building a second one.
 */
export function CreatePlan() {
  const identity = useIdentity();
  const own = identity.namespace ?? "";
  // The operator's own namespace or one of its sites: a plan belongs to the
  // namespace whose subscribers buy it (owner 2026-10-10), and a parent such as
  // net.ankhnet.lotus often has no hotspot of its own -- its sites do.
  const [target, setTarget] = useState(own);
  const [ns, setNs] = useState(own);
  // Owner 2026-10-29: no word of GST for an unregistered or composition operator.
  const { speak } = useGstWording(ns);
  const mayBuild = canSavePlan(identity.roles);
  const [form, setForm] = useState<PlanForm>(EMPTY_FORM);
  const [uid, setUid] = useState(newUid);

  const prices = useQuery({
    queryKey: ["pricelist-components", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: ({ signal }) => listPriceComponents(identity, ns, signal),
  });
  const rows = prices.data ? pickableRows(prices.data) : [];
  const groups = prices.data ? missingDurations(prices.data) : [];
  const [built, setBuilt] = useState<DurationOutcome[]>([]);
  const [allRunning, setAllRunning] = useState(false);
  const putAllOnSale = async () => {
    setAllRunning(true);
    const done: DurationOutcome[] = [];
    for (const g of groups.filter((x) => !x.problem)) done.push(...(await buildDurations(identity, ns, g)));
    setBuilt(done);
    setAllRunning(false);
    void prices.refetch();
  };

  const save = useMutation<PlanSpecResult, Error>({
    mutationFn: () => buildPlan(identity, ns, toRequest(form, uid)),
  });

  const problems = validate(form);
  const ok = Object.keys(problems).length === 0;
  const picked = Boolean(form.componentId);
  const set = <K extends keyof PlanForm>(k: K, v: PlanForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const startAgain = () => {
    setForm(EMPTY_FORM);
    setUid(newUid());
    save.reset();
  };

  const gross = Number(form.price);
  const minutes = form.dailyMinutes.trim() === "" ? null : Number(form.dailyMinutes);

  if (save.data) {
    const r = save.data;
    return (
      <Box sx={{ p: 2, maxWidth: 900 }}>
        <Typography variant="h5" gutterBottom>
          Create plan
        </Typography>
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>Plan {r.groupname} is on sale</AlertTitle>
          {r.heading} · {inr(r.gross)}{speak ? ` incl. GST (${inr(r.net)} + GST ${r.gst_percent}%)` : ""} ·{" "}
          {r.linked ? "priced by the price-list row it was built from" : "with its own price record"}.
        </Alert>
        <Button variant="outlined" onClick={startAgain}>
          Create another plan
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 900 }}>
      <Typography variant="h5" gutterBottom>
        Create plan
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <TextField size="small" label="Namespace" value={target} sx={{ minWidth: 320 }}
          helperText="Your own, or one of your sites -- the hotspot whose subscribers buy this plan"
          onChange={(e) => setTarget(e.target.value.trim().toLowerCase())} />
        <Button variant="outlined" sx={{ height: 40 }}
          onClick={() => {
            setNs(target);
            setForm(EMPTY_FORM);
            setUid(newUid());
            save.reset();
          }}>
          Open
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Built in <code>{ns || "no namespace"}</code>. Validity counts from the subscriber&apos;s first login. A plan&apos;s
        limits never change once built; to change one, build a new plan.
      </Typography>

      {!mayBuild && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>Only a superadmin can save a plan for now</AlertTitle>
          Your role can open this form, but the plan services accept only a superadmin until your operator&apos;s own
          service account exists.
        </Alert>
      )}

      <Typography variant="h6">Start from the price list</Typography>
      {prices.isLoading && <Typography variant="body2">Loading the price list…</Typography>}
      {prices.isError && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The price list could not be read ({(prices.error as Error).message}); type the plan in below.
        </Typography>
      )}
      {prices.data && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No compiled plan prices are waiting for a plan. Type the plan in below.
        </Typography>
      )}
      {rows.length > 0 && (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mb: 2 }} aria-label="Price-list rows">
          {rows.map((row) => (
            <Chip
              key={row.component.id}
              label={`${row.component.name} · ${inr(row.gross)}`}
              color={form.componentId === row.component.id ? "primary" : "default"}
              onClick={() => setForm((f) => pick(f, row))}
            />
          ))}
        </Stack>
      )}

      {built.map((o) => (
        <Alert key={o.componentId} severity={o.error ? "error" : "success"} sx={{ mb: 1 }} onClose={() => setBuilt((b) => b.filter((x) => x !== o))}>
          {o.error ? `${o.name}: ${o.error}` : `${o.name}: plan ${o.groupname} is on sale`}
        </Alert>
      ))}
      {groups.length > 0 && (
        <Box sx={{ mb: 2 }} aria-label="Create all durations">
          <Typography variant="h6">Basic plans not on sale yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Approving a price list puts every basic plan on sale by itself. These price-list plans have no plan yet --
            priced before that, or refused at the time. Each is built at its own price-list price; a speed already on
            sale keeps its devices and daily minutes, any other gets one device (Extra login adds more).
          </Typography>
          <Button variant="contained" sx={{ mb: 1 }} disabled={!mayBuild || allRunning}
            onClick={() => void putAllOnSale()}>
            {allRunning ? "Putting plans on sale…" : `Put all basic plans on sale (${groups.reduce((n, g) => n + (g.problem ? 0 : g.rows.length), 0)})`}
          </Button>
          {groups.map((g) => (
            <AllDurations key={`${ns}|${g.key}`} identity={identity} ns={ns} group={g} mayBuild={mayBuild}
              onBuilt={(done) => {
                setBuilt((b) => [...b.filter((x) => !done.some((d) => d.componentId === x.componentId)), ...done]);
                void prices.refetch();
              }} />
          ))}
        </Box>
      )}

      <Typography variant="h6" sx={{ mt: 1 }}>
        {picked ? "From the price list" : "Or type it in"}
      </Typography>
      {picked && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Speed, days and price come from the price list and are locked.{" "}
          <Button size="small" onClick={() => setForm(unpick)}>
            Type it in instead
          </Button>
        </Typography>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth label="Speed (Mbps)" value={form.speedMbps} disabled={picked}
              error={form.speedMbps !== "" && Boolean(problems.speedMbps)} helperText={problems.speedMbps ?? " "}
              onChange={(e) => set("speedMbps", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth label="Days from first login" value={form.validDays} disabled={picked}
              error={Boolean(problems.validDays)} helperText={problems.validDays ?? " "}
              onChange={(e) => set("validDays", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth label={speak ? "Price incl. GST (₹)" : "Price (₹)"} value={form.price} disabled={picked}
              error={form.price !== "" && Boolean(problems.price)} helperText={problems.price ?? " "}
              onChange={(e) => set("price", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth label="Devices" value={form.devices}
              error={Boolean(problems.devices)} helperText={problems.devices ?? "1 to 10; the price is the same"}
              onChange={(e) => set("devices", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth label="Minutes a day (optional)" value={form.dailyMinutes}
              error={Boolean(problems.dailyMinutes)} helperText={problems.dailyMinutes ?? "Empty: no daily limit"}
              onChange={(e) => set("dailyMinutes", e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField fullWidth label="Heading (optional)" value={form.heading}
              error={Boolean(problems.heading)} helperText={problems.heading ?? "What subscribers see"}
              onChange={(e) => set("heading", e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      {ok && (
        <Alert severity="info" sx={{ mb: 2 }} aria-label="Summary">
          <AlertTitle>
            {planCode(Number(form.speedMbps), Number(form.validDays), Number(form.devices), minutes, uid)}
          </AlertTitle>
          {form.speedMbps} Mbps · {form.validDays} days from first login · {form.devices} device
          {form.devices === "1" ? "" : "s"}
          {minutes ? ` · ${minutes} min a day` : ""} · {inr(gross)}
          {speak ? ` incl. GST (${inr(netOf(gross, form.gstPercent))} + GST ${form.gstPercent}%)` : ""}
          {picked ? " · priced by the price-list row" : ""}. The last four characters are a preview.
        </Alert>
      )}

      {save.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>{failureText(save.error).title}</AlertTitle>
          {failureText(save.error).detail}
        </Alert>
      )}

      <Button variant="contained" disabled={!mayBuild || !ok || save.isPending} onClick={() => save.mutate()}>
        {save.isPending ? "Building…" : "Save"}
      </Button>
    </Box>
  );
}

/** Build each row of a group as its own plan, one after another; never throws. */
async function buildDurations(identity: CallerIdentity, ns: string, g: DurationGroup, onProgress?: (n: number) => void): Promise<DurationOutcome[]> {
  const done: DurationOutcome[] = [];
  for (const row of g.rows) {
    const o: DurationOutcome = { componentId: row.component.id, name: row.component.name };
    try {
      const uid = await durationPlanUid(ns, row.component.id);
      const r = await buildPlan(identity, ns, durationRequest(g, row, uid));
      o.groupname = r.groupname;
    } catch (e) {
      const f = failureText(e as Error);
      o.error = `${f.title}: ${f.detail}`;
    }
    done.push(o);
    onProgress?.(done.length);
  }
  return done;
}

interface DurationOutcome {
  componentId: string;
  name: string;
  groupname?: string;
  error?: string;
}

/**
 * One basic plan's "Create all durations" (owner 2026-10-26). The durations are
 * built one after another through the same POST /plan/{ns} as Save; each has a
 * plan_uid derived from its price record, so pressing it again after a failure
 * finishes the same plans and builds no second one.
 */
function AllDurations(p: {
  identity: CallerIdentity;
  ns: string;
  group: DurationGroup;
  mayBuild: boolean;
  /** Every duration's outcome, once all were tried; the page keeps them after this group is gone. */
  onBuilt: (done: DurationOutcome[]) => void;
}) {
  const g = p.group;
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);

  const run = async () => {
    setRunning(true);
    setProgress(0);
    const done = await buildDurations(p.identity, p.ns, g, setProgress);
    setRunning(false);
    p.onBuilt(done);
  };

  const limits = `${g.devices} device${g.devices === 1 ? "" : "s"}${g.dailyMinutes ? ` · ${g.dailyMinutes} min a day` : ""}`;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
      <Typography variant="subtitle2">
        {g.label}
        {g.base ? ` · copies ${g.base.radcatalog_groupname}` : ""}
        {g.problem ? "" : ` (${limits})`}
      </Typography>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ my: 1 }}>
        {g.rows.map((row) => (
          <Chip key={row.component.id} size="small" variant="outlined" label={`${termWords(row.validDays)} · ${inr(row.gross)}`} />
        ))}
      </Stack>
      {g.problem ? (
        <Typography variant="body2" color="text.secondary">
          Not in one click: {g.problem}.
        </Typography>
      ) : (
        <Button variant="contained" size="small" disabled={!p.mayBuild || running} onClick={() => void run()}>
          {running ? `Creating ${progress + 1} of ${g.rows.length}…` : `Put on sale (${g.rows.length})`}
        </Button>
      )}
    </Paper>
  );
}
